// The one particle system: every spark, smoke puff, flame, ember and dust blob in the game.
// A ring buffer of camera quads whose motion is evaluated in the vertex shader from spawn
// time, velocity, gravity and drag, so spawning writes 28 floats and the whole lot is one
// draw call. Particles may be scheduled in the future (delay) for staggered bursts.
//
// Round 2: smoke is a *lit* puff from the FX atlas (sun-side cream, teal shadow side, gold rim,
// eroding like worn ink as it dies, optionally starting life as a fireball that cools into
// smoke), fire is an atlas flame tongue through a banded heat ramp, and glows/flares fake the
// bloom the post pass doesn't have.

import * as THREE from 'three';
import { Ring, quadGeometry, hazeUniforms, HAZE_VS, HASH_GLSL, ATLAS_GLSL, premulMaterial, fxAtlas } from './gpu';

export const enum PShape {
  Blob = 0, Spark = 1, Chunk = 2, Shard = 3, Hex = 4, Diamond = 5, Ring = 6, Nut = 7, Smoke = 8,
  /** lit atlas smoke puff (colour = albedo); with PFlag.Hot it starts as a fireball */
  Puff = 9,
  /** atlas flame tongue, additive, colour0 = hot core, colour1 = cool edge */
  Fire = 10,
  /** soft additive glow */
  Glow = 11,
  /** star flare */
  Flare = 12,
  /** water droplet (use with Stretch) */
  Drop = 13,
  /** acid bubble: a ring with a highlight */
  Bubble = 14,
  /** ice crystal: faceted bright shard */
  Crystal = 15,
}
export const enum PFlag { Add = 1, Swirl = 2, Stretch = 4, NoHaze = 8, Hot = 16, Up = 32, Soft = 64 }

export type RGB = readonly [number, number, number] | Float32Array;

const STRIDE = 28;

const VS = /* glsl */ `
${HAZE_VS}
attribute vec4 aA; // p0.xyz, t0
attribute vec4 aB; // v.xyz, life
attribute vec4 aC; // gravity, drag, floor, shape
attribute vec4 aD; // size0, size1, spin, flags
attribute vec4 aE; // col0 rgba
attribute vec4 aF; // col1 rgba
attribute float aSeed;
uniform float uTime;
varying vec2 vUv;
varying vec4 vCol;
varying vec4 vCol0;
varying float vShape;
varying float vFlags;
varying float vLife;
varying float vSeed;
varying float vHaze;
varying vec3 vHazeCol;
varying vec2 vRot;
varying vec3 vSunV;
varying float vFlat;
void main() {
  float age = uTime - aA.w;
  float life = aB.w;
  if ( age < 0.0 || age > life ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
  float lt = age / life;
  float flags = aD.w;
  float swirl = mod( floor( flags / 2.0 ), 2.0 );
  float stretch = mod( floor( flags / 4.0 ), 2.0 );
  float nohaze = mod( floor( flags / 8.0 ), 2.0 );
  float upright = mod( floor( flags / 32.0 ), 2.0 );
  vec3 pos; vec3 vel;
  float size = mix( aD.x, aD.y, 1.0 - ( 1.0 - lt ) * ( 1.0 - lt ) );
  if ( swirl > 0.5 ) {
    float ang = aSeed * 6.2832 + aB.z * age;
    float r = aB.x + aC.x * age;
    pos = aA.xyz + vec3( cos( ang ) * r, aB.y * age, sin( ang ) * r );
    vel = vec3( -sin( ang ), 0.0, cos( ang ) ) * r * aB.z + vec3( 0.0, aB.y, 0.0 );
  } else {
    vec3 g = vec3( 0.0, -aC.x, 0.0 );
    float k = max( aC.y, 0.001 );
    float e = exp( -k * age );
    pos = aA.xyz + ( aB.xyz - g / k ) * ( 1.0 - e ) / k + ( g / k ) * age;
    vel = ( aB.xyz - g / k ) * e + g / k;
    pos.y = max( pos.y, aC.z + size * 0.35 );
  }
  // near the camera: fade out from 4 m to 1.5 m, cull inside, and never wider than half the
  // distance (a death or bomb 2-5 m away must not paint the whole screen)
  float camD = length( pos - cameraPosition );
  if ( camD < 1.5 ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
  size = min( size, camD * 0.24 ); // <= ~1/4 of the screen height, whatever is next to the lens
  float near = smoothstep( 1.5, 5.5, camD );
  vec3 right = vec3( viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0] );
  vec3 up = vec3( viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1] );
  vec2 q = position.xy;
  vec3 corner;
  vRot = vec2( 1.0, 0.0 );
  if ( stretch > 0.5 ) {
    vec2 d2 = vec2( dot( vel, right ), dot( vel, up ) );
    float sp = length( d2 );
    d2 = sp > 1e-4 ? d2 / sp : vec2( 0.0, 1.0 );
    vec3 ax = right * d2.x + up * d2.y;
    vec3 sd = right * -d2.y + up * d2.x;
    float len = size * ( 1.0 + min( sp * 0.09, 5.0 ) );
    corner = ax * q.y * len + sd * q.x * size;
  } else if ( upright > 0.5 ) {
    // flames stand up: billboard around world up, wobble a little
    vec3 rgt = normalize( cross( vec3( 0.0, 1.0, 0.0 ), cameraPosition - pos ) + vec3( 1e-4 ) );
    float lean = abs( aC.w - 10.0 ) < 0.5 ? sin( age * 7.0 + aSeed * 40.0 ) * 0.12 : ( aSeed - 0.5 ) * 0.5;
    corner = rgt * ( q.x + q.y * lean ) * size + vec3( 0.0, 1.0, 0.0 ) * q.y * size * 1.25;
  } else {
    float a = aD.z * age + aSeed * 6.2832;
    float c = cos( a ), s = sin( a );
    vRot = vec2( c, s );
    q = vec2( c * q.x - s * q.y, s * q.x + c * q.y );
    corner = ( right * q.x + up * q.y ) * size;
  }
  vec3 wp = pos + corner;
  gl_Position = projectionMatrix * viewMatrix * vec4( wp, 1.0 );
  vUv = position.xy * 2.0;
  vCol = mix( aE, aF, lt );
  vCol.a *= near;
  vCol0 = aE;
  vShape = aC.w; vFlags = flags; vLife = lt; vSeed = aSeed;
  vHaze = nohaze > 0.5 ? 0.0 : hazeAt( pos, vHazeCol );
  vSunV = normalize( ( viewMatrix * vec4( uSunDir, 0.0 ) ).xyz );
  // small on screen (< ~20 px): the lit-puff normals collapse into a dark rim round a pale
  // centre, which reads as a smoke RING at range -- flatten the shading there instead
  vFlat = 1.0 - smoothstep( 0.05, 0.12, size * projectionMatrix[1][1] / max( gl_Position.w, 1e-3 ) );
}
`;

const FS = /* glsl */ `
uniform float uNight; // world/daynight.ts: smoke and dust are moonlit, fire stays hot
${HASH_GLSL}
${ATLAS_GLSL}
varying vec2 vUv;
varying vec4 vCol;
varying vec4 vCol0;
varying float vShape;
varying float vFlags;
varying float vLife;
varying float vSeed;
varying float vHaze;
varying vec3 vHazeCol;
varying vec2 vRot;
varying vec3 vSunV;
varying float vFlat;
// banded heat ramp: deep red -> cool -> hot -> white
vec3 heatRamp( float t, vec3 hot, vec3 cool ) {
  vec3 c = cool * 0.42;
  c = mix( c, cool, smoothstep( 0.22, 0.28, t ) );
  c = mix( c, hot, smoothstep( 0.5, 0.56, t ) );
  c = mix( c, vec3( 1.0, 0.92, 0.7 ) * max( hot.r, 1.0 ) * 1.1, smoothstep( 0.88, 0.93, t ) );
  return c;
}
void main() {
  vec2 p = vUv; // -1..1
  float r = length( p );
  int sh = int( vShape + 0.5 );
  float add = mod( vFlags, 2.0 );
  float a = 0.0;
  float shade = 1.0;
  float core = 0.0;
  vec3 col = vCol.rgb;
  if ( sh == 9 ) {
    // lit atlas puff: undo the billboard spin to sample, then rotate the normal back
    vec2 sp = vec2( vRot.x * p.x + vRot.y * p.y, -vRot.y * p.x + vRot.x * p.y );
    float v = floor( vSeed * 4.0 );
    vec3 t = tile( v, sp * 0.98 );
    float n = vnoise( sp * 3.1 + vSeed * 37.0 );
    float d = t.r * ( 0.72 + 0.56 * n );
    float th = mix( 0.12, 0.98, pow( vLife, 1.4 ) );
    // Soft (64): a feathered vapour with a falloff instead of the hard printed erosion edge
    float soft = mod( floor( vFlags / 64.0 ), 2.0 );
    a = soft > 0.5 ? smoothstep( th * 0.7, th * 0.7 + 0.45, d ) * ( 1.0 - smoothstep( 0.55, 1.0, length( sp ) ) ) * 0.8
                   : smoothstep( th, th + 0.07, d );
    // tiny on screen: the eroded atlas silhouette aliases into a dark ring with a see-through
    // middle; fade to a plain soft disc instead (vFlat also flattens the lighting below)
    a = mix( a, ( 1.0 - smoothstep( 0.3, 0.95, length( sp ) ) ) * ( 1.0 - smoothstep( 0.55, 1.0, vLife ) ) * 0.85, vFlat );
    vec2 nl = ( t.gb - 0.5 ) * 2.0;
    nl = vec2( vRot.x * nl.x - vRot.y * nl.y, vRot.y * nl.x + vRot.x * nl.y );
    vec3 N = normalize( vec3( nl * 2.2, 0.45 ) );
    float l = mix( dot( N, vSunV ), 0.3, vFlat );
    vec3 alb = vCol.rgb;
    vec3 lit = alb * vec3( 1.18, 1.06, 0.88 );
    vec3 mid = alb * vec3( 0.86, 0.85, 0.82 );
    vec3 shd = alb * vec3( 0.5, 0.6, 0.62 ) + vec3( 0.0, 0.006, 0.006 );
    float bw = soft > 0.5 ? 0.3 : 0.04; // soft vapour shades smoothly, printed smoke in hard bands
    col = mix( shd, mid, smoothstep( -0.08 - bw, -0.08 + bw, l ) );
    col = mix( col, lit, smoothstep( 0.24 - bw, 0.24 + bw, l ) );
    // gold rim where the eroding edge faces the sun
    float rim = ( 1.0 - smoothstep( th + 0.05, th + 0.16, d ) ) * smoothstep( 0.0, 0.3, dot( normalize( vec3( nl, 0.2 ) ), vSunV ) );
    col = mix( col, alb * vec3( 1.45, 1.22, 0.85 ) + vec3( 0.06, 0.04, 0.0 ), rim * 0.65 );
    col *= mix( vec3( 1.0 ), vec3( 0.3, 0.36, 0.52 ), uNight );
    if ( mod( floor( vFlags / 16.0 ), 2.0 ) > 0.5 ) {
      // fireball cooling into smoke: hot interior, cooling from the outside in
      float heat = ( 1.0 - smoothstep( 0.05, 0.5, vLife ) ) * ( 0.55 + 0.6 * d ) - ( 1.0 - d ) * 0.35;
      heat += ( 1.0 - smoothstep( 0.0, 0.7, vLife ) ) * smoothstep( 0.5, 1.0, d ) * 0.25;
      vec3 fire = heatRamp( clamp( heat, 0.0, 1.0 ), vec3( 1.9, 1.05, 0.32 ), vec3( 1.25, 0.3, 0.05 ) );
      col = mix( col, fire, smoothstep( 0.2, 0.3, heat ) );
    }
    a *= vCol.a;
    if ( a < 0.01 ) discard;
    col = mix( col, vHazeCol, vHaze * 0.55 );
    gl_FragColor = vec4( col * a, a );
    return;
  }
  if ( sh == 10 ) {
    // flame tongue
    float v = 4.0 + floor( vSeed * 4.0 );
    vec3 t = tile( v, p );
    float flick = vnoise( vec2( p.x * 2.0 + vSeed * 9.0, p.y * 2.0 - vLife * 6.0 ) );
    float d = t.r * ( 0.8 + 0.4 * flick );
    float th = mix( 0.18, 0.95, pow( vLife, 1.2 ) );
    a = smoothstep( th, th + 0.05, d );
    float heat = t.g * ( 1.0 - vLife * 0.6 ) + d * 0.35 - t.b * 0.2;
    col = heatRamp( clamp( heat, 0.0, 1.0 ), vCol0.rgb, vCol.rgb );
    a *= vCol.a;
    if ( a < 0.01 ) discard;
    col *= 1.0 - vHaze * 0.6;
    gl_FragColor = vec4( col * a, 0.0 );
    return;
  }
  if ( sh == 11 || sh == 12 ) {
    vec3 t = tile( sh == 11 ? 8.0 : 9.0, p );
    float g = sh == 11 ? t.r : t.r;
    a = g * vCol.a * ( 1.0 - vLife * vLife );
    if ( a < 0.004 ) discard;
    col = mix( vCol.rgb, vCol0.rgb * 1.3 + 0.2, sh == 11 ? t.g : t.r * t.r );
    col *= 1.0 - vHaze * 0.7;
    gl_FragColor = vec4( col * a, 0.0 );
    return;
  }
  if ( sh == 0 || sh == 8 ) {
    // printed blob: hard wobbly edge, lit top-left, darker bottom-right
    float wob = ( vnoise( p * 2.3 + vSeed * 17.0 ) - 0.5 ) * 0.28;
    a = 1.0 - smoothstep( 0.80, 0.9, r + wob );
    shade = ( p.x * 0.45 - p.y * 0.9 + wob ) < 0.12 ? 1.0 : 0.74;
    core = 1.0 - smoothstep( 0.28, 0.42, r + wob * 0.5 );
    if ( sh == 8 ) {
      float n = vnoise( p * 2.8 + vSeed * 31.0 );
      a *= step( vLife * 0.95, n * 0.85 + 0.25 );
    }
  } else if ( sh == 1 ) {
    // chunky spark: a lozenge with a hot centre
    float d = abs( p.x ) * 1.15 + abs( p.y ) * 0.55;
    a = 1.0 - smoothstep( 0.8, 0.98, d * 1.6 );
    core = 1.0 - smoothstep( 0.35, 0.55, d * 1.6 );
    // alpha sparks carry an ink rim (value contrast on bright ground); additive ones just fade at the edge
    shade = add > 0.5 ? 1.0 : mix( 1.0, 0.08, smoothstep( 0.5, 0.62, d * 1.6 ) );
  } else if ( sh == 2 ) {
    vec2 ap = abs( p );
    a = step( max( ap.x, ap.y * 1.2 ), 0.78 );
    shade = p.x + p.y > 0.0 ? 1.0 : 0.66;
  } else if ( sh == 3 ) {
    float d = max( abs( p.x ) * 1.9 + p.y * 0.9, -p.y );
    a = step( d, 0.8 );
    shade = p.x > 0.0 ? 1.0 : 0.72;
    core = step( d, 0.35 );
  } else if ( sh == 4 ) {
    vec2 ap = abs( p );
    float hx = max( ap.x * 0.866 + ap.y * 0.5, ap.y );
    a = step( hx, 0.85 ) * step( 0.6, hx );
    core = a;
  } else if ( sh == 5 ) {
    float d = abs( p.x ) + abs( p.y );
    a = step( d, 0.9 );
    core = step( d, 0.45 );
  } else if ( sh == 6 ) {
    a = step( r, 0.9 ) * step( 0.6, r );
    core = a;
  } else if ( sh == 7 ) {
    vec2 ap = abs( p );
    float hx = max( ap.x * 0.866 + ap.y * 0.5, ap.y );
    a = step( hx, 0.85 ) * step( 0.3, r );
    shade = p.y > 0.1 ? 1.0 : 0.7;
  } else if ( sh == 13 ) {
    // droplet: round head, tapered tail, a white glint
    float w = mix( 0.75, 0.15, clamp( ( p.y + 0.3 ) / 1.2, 0.0, 1.0 ) );
    a = 1.0 - smoothstep( w - 0.12, w, abs( p.x ) );
    a *= step( -0.9, p.y ) * step( p.y, 0.9 );
    a *= 1.0 - smoothstep( 0.6, 0.9, length( vec2( p.x, min( p.y + 0.15, 0.0 ) ) ) );
    shade = p.x < 0.0 ? 1.0 : 0.72;
    core = step( length( p - vec2( -0.25, -0.2 ) ), 0.2 );
  } else if ( sh == 14 ) {
    // bubble: an inked ring, a translucent fill, a glint; pops at the end of life
    float rr = 0.82 * ( 1.0 + smoothstep( 0.85, 1.0, vLife ) * 0.3 );
    float ring = smoothstep( rr - 0.2, rr - 0.12, r ) * ( 1.0 - smoothstep( rr - 0.04, rr, r ) );
    a = max( ring, step( r, rr ) * 0.35 ) * ( 1.0 - smoothstep( 0.9, 1.0, vLife ) );
    core = step( length( p - vec2( -0.3, 0.3 ) ), 0.16 );
    shade = ring > 0.5 ? 1.0 : 0.8;
  } else if ( sh == 15 ) {
    // ice crystal: a long faceted lozenge, light face and shadow face
    float d = abs( p.x ) * 2.2 + abs( p.y ) * 0.95;
    a = step( d, 0.95 );
    shade = p.x > 0.0 ? 1.0 : 0.62;
    core = step( abs( p.x ), 0.06 ) * step( abs( p.y ), 0.7 );
  }
  a *= vCol.a;
  if ( a < 0.01 ) discard;
  col = vCol.rgb * shade;
  if ( add > 0.5 ) {
    col += core * ( vCol.rgb * 0.6 + vec3( 0.35, 0.3, 0.22 ) ) * vCol.a;
    col *= 1.0 - vHaze * 0.7;
    gl_FragColor = vec4( col * a, 0.0 );
  } else {
    col += core * vec3( 0.25 );
    col = mix( col, vHazeCol, vHaze * 0.5 );
    gl_FragColor = vec4( col * a, a );
  }
}
`;

export class Particles {
  readonly mesh: THREE.Mesh;
  readonly ring: Ring;
  readonly uniforms = { uTime: { value: 0 } };
  constructor(cap: number) {
    this.ring = new Ring(cap, STRIDE);
    const g = quadGeometry(cap);
    const r = this.ring;
    g.setAttribute('aA', r.attr(4, 0));
    g.setAttribute('aB', r.attr(4, 4));
    g.setAttribute('aC', r.attr(4, 8));
    g.setAttribute('aD', r.attr(4, 12));
    g.setAttribute('aE', r.attr(4, 16));
    g.setAttribute('aF', r.attr(4, 20));
    g.setAttribute('aSeed', r.attr(1, 24));
    // all slots start dead
    for (let i = 0; i < cap; i++) { r.arr[i * STRIDE + 3] = -1e6; r.arr[i * STRIDE + 7] = 0; r.arr[i * STRIDE + 24] = Math.random(); }
    const m = premulMaterial(new THREE.ShaderMaterial({
      vertexShader: VS, fragmentShader: FS,
      uniforms: { ...hazeUniforms(), ...this.uniforms, tAtlas: { value: fxAtlas() } },
    }));
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
  }

  time = 0;

  /**
   * One particle. Colours are linear rgb. For swirl particles, (vx,vy,vz) = (radius, rise, omega)
   * and grav = radius growth per second.
   */
  spawn(
    x: number, y: number, z: number, vx: number, vy: number, vz: number,
    life: number, delay: number, grav: number, drag: number, floor: number,
    shape: number, flags: number, size0: number, size1: number, spin: number,
    c0: RGB, a0: number, c1: RGB, a1: number, k0 = 1, k1 = 1,
  ) {
    // a NaN anywhere in an instance becomes NaN pixels, which the HDR bloom smears into blocks
    if (!(x + y + z + vx + vy + vz === x + y + z + vx + vy + vz)) return;
    const a = this.ring.arr, o = this.ring.alloc();
    a[o] = x; a[o + 1] = y; a[o + 2] = z; a[o + 3] = this.time + delay;
    a[o + 4] = vx; a[o + 5] = vy; a[o + 6] = vz; a[o + 7] = life;
    a[o + 8] = grav; a[o + 9] = drag; a[o + 10] = floor; a[o + 11] = shape;
    a[o + 12] = size0; a[o + 13] = size1; a[o + 14] = spin; a[o + 15] = flags;
    a[o + 16] = c0[0] * k0; a[o + 17] = c0[1] * k0; a[o + 18] = c0[2] * k0; a[o + 19] = a0;
    a[o + 20] = c1[0] * k1; a[o + 21] = c1[1] * k1; a[o + 22] = c1[2] * k1; a[o + 23] = a1;
    a[o + 24] = Math.random();
  }

  update(time: number) {
    this.time = time;
    this.uniforms.uTime.value = time;
    this.ring.flush();
  }
}
